/**
 * Module dependencies.
 */

import { Descriptions, Drawer } from 'antd';
import { DateTime } from 'app/components/atoms/date-time/date-time';
import { IdCopy } from 'app/components/atoms/id-copy/id-copy';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { TherapyStatusRenderer } from './therapy-status-renderer';

/**
 * `TherapyLog` type.
 */

type TherapyLog = {
  id: string;
  userId: string;
  requestBody: string;
  responseBody: string | undefined;
  responseStatus: string | undefined;
  error: string | null | undefined;
  createdAt: string;
  updatedAt: string;
};

/**
 * `Props` type.
 */

type Props = {
  item: TherapyLog;
  onClose: () => void;
};

/**
 * Format JSON string for display.
 */

function formatJSON(jsonString: string | null): string {
  if (!jsonString) {
    return 'N/A';
  }

  try {
    const parsed = JSON.parse(jsonString);
    return JSON.stringify(parsed, null, 2);
  } catch {
    return jsonString;
  }
}

/**
 * Export `TherapyLogDetail` component.
 */

export function TherapyLogDetail({ item, onClose }: Props): JSX.Element {
  const [translate] = useTranslation();

  return (
    <Drawer onClose={onClose} open={true} placement={'right'} title={translate('common.labels.logDetails')} width={800}>
      <div style={{ color: '#ffffff' }}>
        <Descriptions bordered column={1} size={'small'}>
          <Descriptions.Item label={translate('common.labels.id')}>
            <IdCopy>{item.id}</IdCopy>
          </Descriptions.Item>

          <Descriptions.Item label={translate('common.labels.userId')}>
            <IdCopy>{item.userId}</IdCopy>
          </Descriptions.Item>

          <Descriptions.Item label={translate('common.labels.responseStatus')}>
            <TherapyStatusRenderer status={item.responseStatus || '0'} />
          </Descriptions.Item>

          <Descriptions.Item label={translate('common.labels.requestBody')}>
            <pre
              style={{
                background: '#1e1e1e',
                padding: '12px',
                borderRadius: '4px',
                overflow: 'auto',
                maxHeight: '300px',
                fontSize: '12px',
                color: '#d4d4d4',
                margin: 0,
                whiteSpace: 'pre-wrap',
                wordBreak: 'break-word'
              }}
            >
              {formatJSON(item.requestBody)}
            </pre>
          </Descriptions.Item>

          <Descriptions.Item label={translate('common.labels.responseBody')}>
            <pre
              style={{
                background: '#1e1e1e',
                padding: '12px',
                borderRadius: '4px',
                overflow: 'auto',
                maxHeight: '400px',
                fontSize: '12px',
                color: '#d4d4d4',
                margin: 0,
                whiteSpace: 'pre-wrap',
                wordBreak: 'break-word'
              }}
            >
              {formatJSON(item.responseBody || '{}')}
            </pre>
          </Descriptions.Item>

          {item.error && (
            <Descriptions.Item label={translate('common.labels.error')}>
              <pre
                style={{
                  background: '#2d1515',
                  padding: '12px',
                  borderRadius: '4px',
                  overflow: 'auto',
                  maxHeight: '300px',
                  fontSize: '12px',
                  color: '#ff6b6b',
                  margin: 0,
                  border: '1px solid #ff6b6b',
                  whiteSpace: 'pre-wrap',
                  wordBreak: 'break-word'
                }}
              >
                {formatJSON(item.error)}
              </pre>
            </Descriptions.Item>
          )}

          <Descriptions.Item label={translate('common.labels.createdAt')}>
            <DateTime date={item.createdAt} showMilliseconds />
          </Descriptions.Item>

          <Descriptions.Item label={translate('common.labels.updatedAt')}>
            <DateTime date={item.updatedAt} showMilliseconds />
          </Descriptions.Item>
        </Descriptions>
      </div>
    </Drawer>
  );
}
